import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { getProfile } from "../api/profile";
import { connectSocket } from "../api/socket";
import { acceptSpaceInviteFromAlert } from "../api/spaces";
import type { NotificationItem } from "../api/notifications";
import {
    notificationPreferenceAllows,
    playNotificationSound,
    primeNotificationSound,
    readNotificationPreferences,
    showActiveBrowserNotification,
    writeNotificationPreferences,
} from "../utils/activeNotifications";

const NOTIFICATIONS_UPDATED_EVENT = "blackspace:notifications-updated";

type NotificationPayload = {
    notification?: NotificationItem;
    unreadCount?: number;
};

function publishNotificationUpdate(unreadCount?: number) {
    window.dispatchEvent(new CustomEvent(NOTIFICATIONS_UPDATED_EVENT, { detail: { unreadCount } }));
}

function isAcceptableSpaceInvite(notification: NotificationItem) {
    return notification.type === "space_invite"
        && notification.metadata?.inviteAction === "accept"
        && typeof notification.metadata?.spaceId === "string"
        && notification.metadata.spaceId.trim().length > 0;
}

function seedNotificationPreferencesFromProfile(settings: Record<string, unknown> | null | undefined) {
    if (!settings) return;
    writeNotificationPreferences({
        pushNotifications: typeof settings.pushNotifications === "boolean" ? settings.pushNotifications : undefined,
        emailNotifications: typeof settings.emailNotifications === "boolean" ? settings.emailNotifications : undefined,
        soundEffects: typeof settings.soundEffects === "boolean" ? settings.soundEffects : undefined,
        notifyMessages: typeof settings.notifyMessages === "boolean" ? settings.notifyMessages : undefined,
        notifySpaces: typeof settings.notifySpaces === "boolean" ? settings.notifySpaces : undefined,
        notifyVault: typeof settings.notifyVault === "boolean" ? settings.notifyVault : undefined,
        notifyMentions: typeof settings.notifyMentions === "boolean" ? settings.notifyMentions : undefined,
        notifyReactions: typeof settings.notifyReactions === "boolean" ? settings.notifyReactions : undefined,
        notifySecurity: typeof settings.notifySecurity === "boolean" ? settings.notifySecurity : undefined,
    });
}

export default function ActiveNotificationBridge() {
    const navigate = useNavigate();
    const handledIdsRef = useRef<Set<string>>(new Set());

    useEffect(() => {
        if (!localStorage.getItem("token")) return;

        let cancelled = false;
        getProfile()
            .then((response) => {
                if (!cancelled) seedNotificationPreferencesFromProfile(response.user?.settings);
            })
            .catch(() => {
                // Defaults/local settings are enough for active notification handling.
            });

        primeNotificationSound();

        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (!localStorage.getItem("token")) return;
        let socket: ReturnType<typeof connectSocket> | null = null;

        const handleNewNotification = (payload: NotificationPayload = {}) => {
            publishNotificationUpdate(payload.unreadCount);

            const notification = payload.notification;
            if (!notification?.id) return;
            if (handledIdsRef.current.has(notification.id)) return;
            handledIdsRef.current.add(notification.id);

            const preferences = readNotificationPreferences();
            if (!notificationPreferenceAllows(notification, preferences)) return;

            if (preferences.soundEffects) {
                void playNotificationSound();
            }

            if (preferences.pushNotifications) {
                showActiveBrowserNotification(notification, async () => {
                    if (isAcceptableSpaceInvite(notification)) {
                        const spaceId = String(notification.metadata?.spaceId || "").trim();
                        try {
                            const result = await acceptSpaceInviteFromAlert(spaceId, notification.id);
                            publishNotificationUpdate(result.unreadCount);
                            navigate(`/spaces/${encodeURIComponent(spaceId)}`, {
                                state: {
                                    notificationType: notification.type,
                                    spaceInviteAccepted: true,
                                },
                            });
                        } catch {
                            navigate("/alerts", {
                                state: {
                                    notificationType: notification.type,
                                    notificationFallback: { title: notification.title, href: notification.href },
                                },
                            });
                        }
                        return;
                    }
                    if (notification.href) {
                        navigate(notification.href, {
                            state: {
                                conversationId: notification.metadata?.conversationId,
                                notificationType: notification.type,
                            },
                        });
                    } else {
                        navigate("/alerts");
                    }
                });
            }
        };

        const handleSummary = (payload: { unreadCount?: number } = {}) => {
            publishNotificationUpdate(payload.unreadCount);
        };

        try {
            socket = connectSocket();
            socket.on("notification:new", handleNewNotification);
            socket.on("notification:summary", handleSummary);
        } catch {
            return;
        }

        return () => {
            socket?.off("notification:new", handleNewNotification);
            socket?.off("notification:summary", handleSummary);
        };
    }, [navigate]);

    return null;
}
